import React from 'react';
import Navigation from '@/components/shared/Navigation';
import Footer from '@/components/shared/Footer';

const StabilizedYieldPower = () => {
  return (
    <div className="min-h-screen bg-[#F9FAFB] font-sans animate-slide-in">
      <Navigation />

      {/* Article Header */}
      <section className="bg-white py-16 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-sm text-gray-500 mb-3">
            Real Estate Strategy · PulseShift Research
          </p>

          <h1 className="text-4xl font-semibold text-[#0A2540] mb-4">
            Why Most Investors Miscalculate Success
          </h1>

          <p className="text-xl text-[#2E2E2E] max-w-3xl">
            The power of Stabilized Yield — and why institutional investors
            underwrite outcomes, not snapshots.
          </p>
        </div>
      </section>

      {/* Article + PDF */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row gap-12">

          {/* LEFT — TEXT */}
          <div className="lg:w-[58%] space-y-10 text-[#2E2E2E] leading-relaxed">

            <h2 className="text-2xl font-semibold text-[#0A2540]">
              The Illusion of “Current Yield”
            </h2>

            <p>
              In Real Estate Private Equity, a property’s current yield is often
              little more than a snapshot in time — and frequently a deceptive
              one. Retail investors routinely dismiss assets with weak
              entry-level income, while institutional investors identify these
              same properties as alpha-generating opportunities.
            </p>

            <p>
              The difference is not optimism. It is underwriting discipline.
              Professionals underwrite the <strong>pro-forma reality</strong>,
              not the current performance.
            </p>

            <h2 className="text-2xl font-semibold text-[#0A2540]">
              Stabilized Yield: The Architectural Blueprint
            </h2>

            <p>
              Stabilized Yield — also known as Yield-on-Cost (YOC) — is the metric
              that separates institutional execution from amateur speculation.
              It defines the expected return once capital has been deployed and
              the business plan is fully realized.
            </p>

            <p>
              Renovation, repositioning, lease-up, and operational correction
              are not noise. They are the value-creation mechanism.
            </p>

            <h2 className="text-2xl font-semibold text-[#0A2540]">
              The Hidden Denominator: Total Project Cost
            </h2>

            <p>
              A common analytical failure is calculating yield using only the
              acquisition price. True Stabilized Yield must be measured against
              <strong> Total Project Cost (TPC)</strong> — the full capital basis
              required to reach a steady state.
            </p>

            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Acquisition Cost:</strong> Contract purchase price</li>
              <li><strong>Closing Fees:</strong> Legal, diligence, transfer taxes</li>
              <li><strong>Renovation (CAPEX):</strong> Hard and soft repositioning costs</li>
              <li><strong>Carry Costs:</strong> Interest, taxes, insurance during execution</li>
            </ul>

            <p>
              Carry costs are the most frequently ignored input — and the most
              dangerous. These are cash-out items that add no physical value but
              directly erode equity.
            </p>

            <h2 className="text-2xl font-semibold text-[#0A2540]">
              The Development Spread Is the Real Profit
            </h2>

            <p>
              Stabilized Yield becomes powerful only when compared against the
              prevailing Market Cap Rate. If you build to an 8% yield in a 6%
              market, you have engineered a 200 basis-point development spread.
            </p>

            <blockquote className="border-l-4 border-[#4A6FA5] pl-4 italic text-gray-600">
              “It represents the profit you created by taking execution risk.”
            </blockquote>

            <p>
              This spread is not just upside — it is protection. Even if exit
              cap rates expand, the margin you engineered absorbs the shock.
            </p>

            <h2 className="text-2xl font-semibold text-[#0A2540]">
              The Warehouse Conversion: A Reality Check
            </h2>

            <ul className="list-disc pl-6 space-y-2">
              <li>Acquisition Price: $5,000,000</li>
              <li>Renovation & Leasing Basis: $3,000,000</li>
              <li>Total Project Cost: $8,000,000</li>
              <li>Stabilized NOI: $640,000</li>
              <li>Stabilized Yield: 8.0%</li>
            </ul>

            <p>
              In a 6% cap-rate market, the stabilized value is $10.67M. By
              deploying $8M, the investor validates the risk with immediate
              embedded value.
            </p>

            <h2 className="text-2xl font-semibold text-[#0A2540]">
              When Static Math Fails
            </h2>

            <p>
              Stabilized Yield is static. Time is not. An 8% yield achieved in
              twelve months is fundamentally different from the same yield
              achieved after five years of capital lock-up.
            </p>

            <p>
              Long execution timelines magnify opportunity cost and expose
              projects to exit risk. A great yield on paper is irrelevant if
              the market moves before stabilization.
            </p>

            <h2 className="text-2xl font-semibold text-[#0A2540]">
              Engineering Long-Term Value
            </h2>

            <p>
              The most sophisticated investors use Yield-on-Cost not just at
              entry, but across portfolio life cycles. Over time, stabilized
              yield compounds into defensive, locked-in performance.
            </p>

            <p className="font-semibold">
              Ask yourself: is your portfolio riding market momentum — or built
              to survive it?
            </p>

            <p className="text-sm text-gray-500 pt-6">
              Source: PulseShift Research | Real Estate Strategy
            </p>
          </div>

          {/* RIGHT — PDF */}
          <div className="lg:w-[42%] sticky top-24 self-start">
            <div className="h-[85vh] border rounded-lg overflow-hidden shadow-sm">
              <iframe
                src="/Real_Estate_Value_Architecture.pdf#view=FitH&navpanes=0&scrollbar=0"
                title="Stabilized Yield & Value Architecture PDF"
                className="w-full h-full"
              />
            </div>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StabilizedYieldPower;
